import Controllers from './Controllers.js';
import Helpers from './Helpers.js';
import Chat from './Chat.js';
import Book from './Book.js';
import Support from './Support.js';
import OneMind from './1mind.js';

/**
 * EventHandlers — implementations for the Events registry.
 *
 * Every handler receives (event, component) where component is the Alpine
 * instance from ThryvConnectBar.data(). Analytics: each action dispatches a
 * `connect_bar_click` event via Helpers.dispatch (site tracker → dataLayer),
 * so GTM can pick them up without new code here.
 *
 * Chili Piper integration plumbing lives in its own modules — Chat.js
 * (chat widget bootstrap/state sync) and Book.js (hosted scheduler
 * events); handlers here only do UI concerns and delegate.
 */

var EventHandlers = new Object();

/**
 * The bar button that opened the current dialog — focus returns to it on
 * close (see moveFocus/restoreFocus).
 */
EventHandlers.returnFocus = null;

/**
 * Run-once guard for the scroll listener (armed by watchScroll).
 */
EventHandlers.scrollWatching = false;

/**
 * Timers: the Book panel's resize-fade cleanup and the Call modal's
 * "copied" check-mark reset.
 */
EventHandlers.bookResizingTimer = null;
EventHandlers.phoneCopiedTimer = null;
EventHandlers.oneMindResizingTimer = null;

/**
 * Shared analytics dispatch for all bar actions.
 *
 * @param {string} action     book|chat|call|support
 * @param {object} component  Alpine component
 */
EventHandlers.track = function (action, component) {

  Helpers.dispatch('connect_bar_click', {
    event: 'connect_bar_click',
    event_name: 'connect_bar_click',
    connect_bar_action: action,
    page_url: window.location.href,
  });

  Helpers.log(component, 'track', action);

};

/**
 * Book — open Chili Piper's hosted scheduler in the Book modal iframe
 * (the flow's "In Emails" scheduling link — calendar-first, the visitor
 * books entirely inside CP's UI, no HubSpot form). The iframe mounts on
 * the first click (`bookLoaded`) and stays mounted, so later opens are
 * instant and a picked-but-unbooked slot survives closing the modal.
 */
EventHandlers.bookClick = function (event, component) {

  var _this = this;

  _this.track('book', component);

  if (!component.bookingUrl) {

    Helpers.log(component, 'bookClick — no bookingUrl configured');

    return;

  }

  // Toggle: clicking Book while its panel is open closes it.
  if (component.bookModalOpen) {

    component.bookModalOpen = false;

    return;

  }

  // One panel at a time — Book replaces the Call modal, CP chat, the AI
  // Advisor panel, and the Intercom messenger if any is open.
  component.callModalOpen = false;

  Chat.hide(component);
  OneMind.hide(component);

  if (component.supportOpen && Controllers.intercomAvailable()) window.Intercom('hide');

  // Spinner only on the first open — hidden when the scheduler posts
  // availability-loaded (real slots-rendered signal; see Book.widgetEvent).
  // Later opens reuse the mounted iframe, so no spinner.
  if (!component.bookLoaded) component.bookFrameLoading = true;

  component.bookLoaded = true;
  component.bookModalOpen = true;

  _this.moveFocus(event, '.thryv-connect-bar__panel-close');

};

/**
 * Book iframe load (bound in book-panel.php) — the CP page arrived but the
 * slots grid hasn't rendered yet; Book.frameLoaded arms the spinner
 * fallback in case availability-loaded never comes.
 */
EventHandlers.bookFrameLoad = function (event, component) {

  Book.frameLoaded(component);

};

/**
 * Close the Book modal (X button / backdrop click).
 */
EventHandlers.bookModalClose = function (event, component) {

  var _this = this;

  component.bookModalOpen = false;

  _this.restoreFocus();

};

/**
 * Resize toggle in the Book panel header — widens the panel to the size
 * where CP's fixed-width round-robin page fits without a horizontal
 * scroll (~560px; see the panel width note in sass/index.scss).
 */
EventHandlers.bookResizeToggle = function (event, component) {

  var _this = this;

  component.bookExpanded = !component.bookExpanded;

  // Fade the panel content while the size transition runs (matches the
  // .3s width/height ease in sass/index.scss, small buffer for cleanup).
  component.bookResizing = true;

  clearTimeout(_this.bookResizingTimer);

  _this.bookResizingTimer = setTimeout(function () {

    component.bookResizing = false;

  }, 350);

};

/**
 * Escape key — close whichever modal is open, returning focus to the
 * button that opened it.
 */
EventHandlers.escapeClose = function (event, component) {

  var _this = this;

  component.callModalOpen = false;
  component.bookModalOpen = false;

  Chat.hide(component);
  OneMind.hide(component);

  _this.restoreFocus();

};

/**
 * Collapse toggle — tucks the pill into the small round launcher (and
 * back). The visitor's choice persists for the session; collapsing also
 * closes whatever is open.
 */
EventHandlers.collapseToggle = function (event, component) {

  var _this = this;

  component.collapsed = !component.collapsed;

  try {

    sessionStorage.setItem('thryv_cb_collapsed', component.collapsed ? '1' : '0');

  } catch (e) { /* private mode — state just won't persist */ }

  _this.track(component.collapsed ? 'collapse' : 'expand', component);

  if (!component.collapsed) return;

  component.bookModalOpen = false;
  component.callModalOpen = false;

  Chat.hide(component);
  OneMind.hide(component);

  if (component.supportOpen && Controllers.intercomAvailable()) window.Intercom('hide');

};

/**
 * Scroll intent — hide the bar while the visitor reads downward, bring it
 * back the moment they scroll up (a pause/re-orient gesture). Never hides
 * near the top of the page or while any panel is open.
 */
EventHandlers.watchScroll = function (component) {

  var _this = this;

  if (_this.scrollWatching) return;

  _this.scrollWatching = true;

  var lastY = window.scrollY;
  var ticking = false;

  window.addEventListener('scroll', function () {

    if (ticking) return;

    ticking = true;

    requestAnimationFrame(function () {

      ticking = false;

      var y = window.scrollY;
      var delta = y - lastY;

      if (Math.abs(delta) < 8) return; // ignore sub-threshold jitter

      lastY = y;

      if (component.bookModalOpen || component.callModalOpen || component.chatOpen || component.supportOpen) {

        component.scrollHidden = false;

        return;

      }

      component.scrollHidden = delta > 0 && y > 200;

    });

  }, { passive: true });

};

/**
 * Focus management for the Book/Call dialogs — move focus to the dialog's
 * close button on open (screen reader / keyboard users land inside), and
 * restore it to the invoking bar button on close.
 */
EventHandlers.moveFocus = function (event, selector) {

  var _this = this;

  _this.returnFocus = event && event.currentTarget;

  setTimeout(function () {

    var target = document.querySelector(selector);

    if (target) target.focus({ preventScroll: true });

  }, 50);

};

EventHandlers.restoreFocus = function () {

  var _this = this;

  if (_this.returnFocus && document.contains(_this.returnFocus)) {

    _this.returnFocus.focus({ preventScroll: true });

  }

  _this.returnFocus = null;

};

/**
 * Chat button — CP's Custom Trigger (configured on #thryv-connect-bar-chat
 * in CP admin) opens the chat journey natively from this same click; this
 * handler only tracks, toggles, and closes the other panels. The CP
 * plumbing (state sync, suppression) lives in Chat.js.
 */
EventHandlers.chatClick = function (event, component) {

  var _this = this;

  _this.track('chat', component);

  if (component.chatState !== 'ready') return;

  // Toggle: clicking Chat while its widget is visible hides it (CP has no
  // close API — Chat.hide suppresses the state sync instead).
  if (component.chatOpen) {

    Chat.hide(component);

    return;

  }

  // One panel at a time — Chat replaces the Book/Call panels, the AI
  // Advisor panel, and the Intercom messenger.
  component.bookModalOpen = false;
  component.callModalOpen = false;

  OneMind.hide(component);

  if (component.supportOpen && Controllers.intercomAvailable()) window.Intercom('hide');

  Chat.open(component);

};

/**
 * Call — open the phone modal (number comes from per-site template config).
 */
EventHandlers.callClick = function (event, component) {

  var _this = this;

  _this.track('call', component);

  // Toggle: clicking Call while its modal is open closes it.
  if (component.callModalOpen) {

    component.callModalOpen = false;

    return;

  }

  // One panel at a time — Call replaces the Book panel, CP chat, the AI
  // Advisor panel, and the Intercom messenger if any is open.
  component.bookModalOpen = false;

  Chat.hide(component);
  OneMind.hide(component);

  if (component.supportOpen && Controllers.intercomAvailable()) window.Intercom('hide');

  component.callModalOpen = true;

  _this.moveFocus(event, '.thryv-connect-bar__modal-close');

};

/**
 * Close the phone modal (X button / backdrop click).
 */
EventHandlers.callModalClose = function (event, component) {

  var _this = this;

  component.callModalOpen = false;

  _this.restoreFocus();

};

/**
 * Copy the phone number from the Call modal to the clipboard, with a brief
 * check-mark confirmation on the button.
 */
EventHandlers.phoneCopyClick = function (event, component) {

  var _this = this;

  _this.track('call_copy', component);

  if (!navigator.clipboard) {

    Helpers.log(component, 'phoneCopyClick — clipboard API unavailable');

    return;

  }

  navigator.clipboard.writeText(component.phoneNumber).then(function () {

    component.phoneCopied = true;

    clearTimeout(_this.phoneCopiedTimer);

    _this.phoneCopiedTimer = setTimeout(function () {

      component.phoneCopied = false;

    }, 1500);

  }).catch(function (error) {

    Helpers.log(component, 'phoneCopyClick — copy failed', error);

  });

};

/**
 * Support — toggle the Intercom messenger open/closed. No-ops (with a
 * debug note) on pages where Intercom is disabled via the page setting.
 */
EventHandlers.supportClick = function (event, component) {

  var _this = this;

  _this.track('support', component);

  if (!Controllers.intercomAvailable()) {

    Helpers.log(component, 'supportClick — Intercom not available on this page');

    return;

  }

  // Positioning + supportOpen sync are armed at init (Support.hook); this
  // is only a safety for the unlikely click-before-hook race.
  Support.hook(component);

  if (component.supportOpen) {

    window.Intercom('hide');

  } else {

    // One panel at a time — the messenger replaces the Book/Call panels,
    // CP chat, and the AI Advisor panel.
    component.bookModalOpen = false;
    component.callModalOpen = false;

    Chat.hide(component);
    OneMind.hide(component);

    window.Intercom('show');

  }

};

/**
 * AI Advisor — open the 1mind Superhuman in its own bar panel. The launcher
 * is injected on this first click (OneMind.open), not at page load, so the
 * ~64KB download and the 1mind session only happen for visitors who ask.
 */
EventHandlers.oneMindClick = function (event, component) {

  var _this = this;

  _this.track('1mind', component);

  // Toggle: clicking AI Advisor while its panel is open closes it. The
  // iframe stays mounted, so re-opening resumes the same conversation.
  if (component.oneMindOpen) {

    OneMind.hide(component);

    return;

  }

  // One panel at a time — the AI Advisor replaces the Book/Call panels, CP
  // chat, and the Intercom messenger.
  component.bookModalOpen = false;
  component.callModalOpen = false;

  Chat.hide(component);

  if (component.supportOpen && Controllers.intercomAvailable()) window.Intercom('hide');

  OneMind.open(component);

  _this.moveFocus(event, '.thryv-connect-bar__panel--1mind .thryv-connect-bar__panel-close');

};

/**
 * Close the AI Advisor panel (X button).
 */
EventHandlers.oneMindPanelClose = function (event, component) {

  var _this = this;

  OneMind.hide(component);

  _this.restoreFocus();

};

/**
 * Resize toggle in the AI Advisor panel header. 1mind recommends 70-85% of
 * the viewport for the Superhuman; the bar's default panel width is far
 * narrower, so this is the affordance that gets it to a usable size (see
 * the 1mind panel width note in sass/index.scss).
 */
EventHandlers.oneMindResizeToggle = function (event, component) {

  var _this = this;

  component.oneMindExpanded = !component.oneMindExpanded;

  // Fade the panel content while the size transition runs, matching the
  // Book panel's behaviour.
  component.oneMindResizing = true;

  clearTimeout(_this.oneMindResizingTimer);

  _this.oneMindResizingTimer = setTimeout(function () {

    component.oneMindResizing = false;

  }, 350);

};

export default EventHandlers;
